import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/utils/formatters';
import { FileText, TrendingUp, TrendingDown, Target, BarChart3, DollarSign, Calendar } from 'lucide-react';

interface FileContribution {
  filename: string;
  fileId: string;
  totalPnl: number;
  tradeCount: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: number;
  maxTP: number;
  maxSL: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
  dateRange: {
    start: string;
    end: string;
  };
}

interface IndividualFilePerformanceProps {
  fileContributions: FileContribution[];
}

export const IndividualFilePerformance: React.FC<IndividualFilePerformanceProps> = ({
  fileContributions,
}) => {
  if (!fileContributions || fileContributions.length === 0) {
    return null;
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const getPerformanceColor = (pnl: number) => {
    if (pnl > 0) return 'text-success';
    if (pnl < 0) return 'text-destructive';
    return 'text-muted-foreground';
  };

  const getWinRateColor = (winRate: number) => {
    if (winRate >= 60) return 'bg-success/20 text-success';
    if (winRate >= 50) return 'bg-warning/20 text-warning';
    return 'bg-destructive/20 text-destructive';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Individual File Performance
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {fileContributions.map((file, index) => (
            <div key={file.fileId} className="border rounded-lg p-4 bg-muted/20 hover:bg-muted/30 transition-colors">
              {/* File Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <h4 className="font-medium text-foreground truncate">{file.filename}</h4>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={file.totalPnl >= 0 ? "default" : "destructive"} className="font-mono">
                    {formatCurrency(file.totalPnl)}
                  </Badge>
                  <Badge variant="outline" className={getWinRateColor(file.winRate)}>
                    {file.winRate.toFixed(1)}% Win
                  </Badge>
                </div>
              </div>

              {/* Statistics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {/* Total Profit/Loss */}
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <DollarSign className="h-3 w-3" />
                    <span>Total PnL</span>
                  </div>
                  <div className={`font-mono font-medium ${getPerformanceColor(file.totalPnl)}`}>
                    {formatCurrency(file.totalPnl)}
                  </div>
                </div>

                {/* Trade Count */}
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <BarChart3 className="h-3 w-3" />
                    <span>Trades</span>
                  </div>
                  <div className="font-medium">{file.tradeCount}</div>
                </div>

                {/* Average Win */}
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <TrendingUp className="h-3 w-3" />
                    <span>Avg Win</span>
                  </div>
                  <div className="font-mono text-success">
                    {formatCurrency(file.avgWin)}
                  </div>
                </div>

                {/* Average Loss */}
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <TrendingDown className="h-3 w-3" />
                    <span>Avg Loss</span>
                  </div>
                  <div className="font-mono text-destructive">
                    {formatCurrency(-file.avgLoss)}
                  </div>
                </div>

                {/* Max TP */}
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Target className="h-3 w-3" />
                    <span>Max TP</span>
                  </div>
                  <div className="font-mono text-success">
                    {formatCurrency(file.maxTP)}
                  </div>
                </div>

                {/* Max SL */}
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Target className="h-3 w-3" />
                    <span>Max SL</span>
                  </div>
                  <div className="font-mono text-destructive">
                    {formatCurrency(file.maxSL)}
                  </div>
                </div>

                {/* Profit Factor */}
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <BarChart3 className="h-3 w-3" />
                    <span>Profit Factor</span>
                  </div>
                  <div className={`font-medium ${file.profitFactor >= 1 ? 'text-success' : 'text-destructive'}`}>
                    {file.profitFactor.toFixed(2)}
                  </div>
                </div>

                {/* Max Drawdown */}
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <TrendingDown className="h-3 w-3" />
                    <span>Max DD</span>
                  </div>
                  <div className="font-mono text-destructive">
                    {formatCurrency(file.maxDrawdown)} ({file.maxDrawdownPercent.toFixed(1)}%)
                  </div>
                </div>
              </div>

              {/* Date Range */}
              <div className="mt-3 pt-3 border-t border-border/50">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  <span>Period: {formatDate(file.dateRange.start)} - {formatDate(file.dateRange.end)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};